import React from 'react';
import { Button, SearchInput, WindowedTable as Table } from '@economic/taco';

import useCachedQuery from '../shared/hooks/useCachedQuery';
import { ViewProps } from './types';
import { t, useLocalization } from '../shared/components/l10n/l10n';
import View, { useViewFilter } from '../shared/components/View/View';
import { useTableSortPreferences } from '../shared/hooks/useTableSortPreference';
import Loader from '../shared/components/Loader';
import { CreateProductCategoryDialog } from '../forms/Inventory/CreateProductCategory';
import { ProductCategory } from '../../types/internal-api';

export default function ProductCategoriesView({
    onSelected: handleSelected,
    onCreated: handleCreated,
}: ViewProps<ProductCategory>) {
    const loadingl10n = useLocalization(textIds);
    const productCategoriesQuery = useCachedQuery<ProductCategory[]>('/products/categories', []);
    const [filter, setFilter, filterBy] = useViewFilter();
    const tableSort = useTableSortPreferences('productCategories');

    if (loadingl10n) {
        return <Loader />;
    }

    const createButton = (
        <Button
            appearance="primary"
            dialog={props => (
                <CreateProductCategoryDialog
                    onSubmitted={productCategory => {
                        productCategoriesQuery.refetch();

                        if (handleCreated) {
                            handleCreated(productCategory);
                        }
                    }}
                    {...props}
                />
            )}>
            {t`FORM_CATEGORY_CREATETEXT`}
        </Button>
    );

    return (
        <View>
            <View.Title>{t`FORM_CATEGORY_HEADLINE`}</View.Title>

            <View.Header>
                <View.Header.Actions>{createButton}</View.Header.Actions>
                <View.Header.Filters>
                    <SearchInput
                        autoFocus
                        onChange={event => setFilter(event.target.value)}
                        placeholder={t`FORM_LISTVIEW_FILTER_PLACEHOLDER`}
                        value={filter}
                    />
                </View.Header.Filters>
            </View.Header>

            <View.Content>
                <Table<ProductCategory>
                    {...tableSort}
                    data={productCategoriesQuery.data.filter(
                        productCategory => filterBy(productCategory.number) || filterBy(productCategory.name)
                    )}
                    onRowClick={row => handleSelected(row.values)}
                    emptyStateRenderer={() =>
                        !productCategoriesQuery.loading && productCategoriesQuery.data.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <p className="text-2xl font-hairline">{t`FORM_CATEGORIES_EMPTY_STATE`}</p>
                                <p>{createButton}</p>
                            </div>
                        ) : null
                    }
                    autoFocus>
                    <Table.Column accessor="number" headRenderer={t`FORM_FIELD_LABEL_NUMBER`} flex="0 0 75px" sort="asc" />
                    <Table.Column accessor="name" align="left" headRenderer={t`FORM_FIELD_LABEL_NAME`} />
                </Table>
            </View.Content>
        </View>
    );
}

const textIds = ['FORM_CATEGORIES_EMPTY_STATE', 'FORM_CATEGORY_CREATETEXT'];
